import { Observable } from "rxjs";

import { post, put, get } from "@utils/axios";
import sessionService from "./session.service";

class CareCoordinationService {
  /***************** Initial Load APIs *********************** */

  fetchProgramOptions({ patientId }: any): Observable<any> {
    return get(`${sessionService.getClient()?.appServiceUrl}/care-coordination/program/options?patientId=${patientId}`);
  }

  fetchProgramDetails({ programId = null, patientProgramId = null, patientId, startDate = null, instanceNeeded = null, programType }: any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/program/details`, {
      programId,
      patientProgramId,
      patientId,
      startDate,
      instanceNeeded,
      programType,
    });
  }
  /**************** TASK RELATED APIS ******************** */

  deleteTask({ patientId, interventionId, taskId }: any): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/task/delete`, {
      patientId: patientId,
      interventionId: interventionId,
      taskId: taskId,
    });
  }

  updateTask({ patientId, programId, interventionId, status, taskId, value, taskCreateDate, taskEndDate }: any): Observable<any> {
    const response = put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/task/edit`, {
      patientId: patientId,
      interventionId: interventionId,
      value: value,
      status: status,
      id: taskId,
      programId: programId,
      startDate: taskCreateDate,
      endDate: taskEndDate,
    });
    return response;
  }

  updateSubTask({ patientId, interventionId, taskId, value, subTaskId }: any): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/subTask/edit`, {
      patientId: patientId,
      interventionId: interventionId,
      value: value,
      // taskStatus: status,
      taskId: taskId,
      id: subTaskId,
    });
  }

  createNewTask({ patientId, interventionId, taskId, patientProgramId }: any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/task/create`, {
      patientId: patientId,
      interventionId: interventionId,
      taskId: taskId,
      patientProgramId: patientProgramId,
    });
  }

  fetchTaskList({ patientId, programId, patientProgramId }: any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/task/options`, {
      patientId: patientId,
      programId: programId,
      patientProgramId: patientProgramId,
    });
  }

  /**************** Barrier RELATED APIS ******************** */

  createNewBarrier({ patientId, interventionId, taskId, barrierId = null, description }: any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/barrier/create`, {
      patientId: patientId,
      interventionId: interventionId,
      taskId: taskId,
      barrierId: barrierId,
      barrierDescription: description,
    });
  }

  fetchGlobalBarrierList({ patientId }: any): Observable<any> {
    return get(`${sessionService.getClient()?.appServiceUrl}/care-coordination/barrier/search?patientId=${patientId}`);
  }

  updateBarrier({ patientId, interventionId, taskId, barrierId, status, description, globalFlag }: any): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/barrier/update`, {
      patientId: patientId,
      interventionId: interventionId,
      taskId: taskId,
      barrierId: barrierId,
      barrierDescription: description,
      isGlobal: globalFlag,
      status: status,
    });
  }

  completeAllBarriers({ patientId, interventionId, taskId, barrierIds }: any): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/barrier/complete`, {
      barrierIds: barrierIds,
      patientId: patientId,
      interventionId: interventionId,
      taskId: taskId,
    });
  }

  deleteBarrier({ patientId, interventionId, taskId, barrierId }: any): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/barrier/delete`, {
      barrierId: barrierId,
      patientId: patientId,
      interventionId: interventionId,
      taskId: taskId,
    });
  }
  /**************** Goal RELATED APIS ******************** */

  createNewGoal({ patientId, interventionId, description , isGlobal}: any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/goal/create`, {
      patientId: patientId,
      interventionId: interventionId,
      goalDescription: description,
      isGlobal : isGlobal
    });
  }

  fetchGlobalGoalList({ patientId }: any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/goal/search`, {
      memberId: patientId
    });
  }

  updateGoal({ patientId, interventionId, goalId, status, description, globalFlag, isTemplate }: any): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/goal/update`, {
      patientId: patientId,
      interventionId: interventionId,
      goalId: goalId,
      goalDescription: description,
      isGlobal: globalFlag,
      isTemplate: isTemplate,
      status: status,
    });
  }

  completeAllGoals({ patientId, interventionId, goalIds }: any): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/goal/complete`, {
      goalIds: goalIds,
      patientId: patientId,
      interventionId: interventionId,
    });
  }

  deleteGoal({ patientId, interventionId, goalId , goalNotes}: any): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/goal/delete`, {
      goalId: goalId,
      patientId: patientId,
      interventionId: interventionId,
      goalNotes : goalNotes
    });
  }

  /**************** Note RELATED APIS ******************** */

  createNote(notes: any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/note/create`, { ...notes });
  }

  editNote(notes: any): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/note/edit`, { ...notes });
  }

  removeNote(notes: any): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/note/delete`, { ...notes });
  }

  //// Care-co-ordination reminder service
  createReminder(reminder: any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/reminder/create`, { ...reminder });
  }

  editReminder(reminder: any): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/reminder/edit`, { ...reminder });
  }

  deleteReminder(reminder: any , operationRange : string): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/reminder/delete`, { ...reminder , operationRange});
  }

  /**************** Intervention RELATED APIS ******************** */

  deleteIntervention(intervention: any): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/intervention/delete`, { ...intervention });
  }

  updateIntervention(intervention: any): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/intervention/update`, { ...intervention});
  }

  /**************** Program RELATED APIS ******************** */

  updateProgram(program: any): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/program/update`, { ...program });
  }

  createNewProgram({ patientId, programId = null, programName = null, startDate, endDate , prgmMetadata = {} ,prgmGrpMetadata ={}, prgmGrpId}: any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/program/assign`, {
      patientId,
      programId: +programId,
      programName,
      startDate,
      endDate,
      prgmMetaData : prgmMetadata,
      prgmGrpMetadata,
      prgmGrpId
    });
  }

  calculateScore({ patientProgramId, patientId, programId, interventionId, taskId, subTaskId, name, param1, param2, param3, param4, param5, param6 }: any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/subtask/score`, {
      patientProgramId,
      patientId,
      programId,
      interventionId,
      taskId,
      subtaskId: subTaskId,
      name,
      param1,
      param2,
      param3,
      param4,
      param5,
      param6,
    });
  }

  updateInterventionDetails({ patientId, interventionId, detailId, detailValue }: any): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/interventionDetail/update`, { patientId, interventionId, detailId, detailValue });
  }

  // deleteProgram({ patientId, patientProgramId= null , programId = null}: any): Observable<any> {
  //   return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/program/delete`, {
  //     patientId,
  //     // programId: programId,
  //     patientPgmId : patientProgramId
  //   });
  // }

  fetchAssigneeList(): Observable<any> {
    return get(`${sessionService.getClient()?.appServiceUrl}/care-coordination/high-intensity-program/fetch-assignees`);
  }
  fetchDriversList({ patientId, patientProgramId, programId }: any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/high-intensity-program/fetch-drivers`, {
      patientProgramId: patientProgramId,
      programId: programId,
      patientId: patientId,
    });
  }

  updateAssignee({ patientId, payload, notificationPayload }: any): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/high-intensity-program/update-assignee?patientId=${patientId}`, {
      updateData: payload,
      notificationData: notificationPayload,
      isReminder: false,
    });
  }

  sendReminder({ patientId, payload, notificationPayload }: any): Observable<any> {
    return put(`${sessionService.getClient()?.appServiceUrl}/care-coordination/high-intensity-program/update-assignee?patientId=${patientId}`, {
      updateData: payload,
      notificationData: notificationPayload,
      isReminder: true,
    });
  }

  fetchSubTaskHistory({ taskId, date, flag }: any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/subtask/history`, {
      taskId,
      date,
      flag,
    });
  }

  createTaskHistory({ taskId }: any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/subtask/create-history`, {
      taskId,
    });
  }
  fetchReassignmentAssigneeList(patientId : number): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/task/assignee-list`, {
      memberId : patientId
    });
  }
  updateAssignedStatus({ patientId, patientProgramId, programId, userId }: any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/task/update-program-assignment`, {
      patientId,
      patientProgramId,
      programId,
      userId
    });
  }
  updateReminderStatus({ patientId, assignedUserId, tasksId, subtasksId }: any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/task/update-reminder-assignment`, {
      patientId,
      assignedUserId,
      tasksId,
      subtasksId
    });
  }

  addUpdateEmrNote(payload: any) {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/care-plan-note`, {
      ...payload
    });
  }

  fetchCarePlanEMRNotes({patientId}: any) {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/care-plan-emr-notes`, {
      memberId: patientId
    });
  }

  uploadDocsToAthena({ practiceId, patientId, departmentId, providerId, attachmentContents , programs}: any) {
    return post(`${sessionService.getClient()?.appServiceUrl}/patient/athena/upload-docs`, {
      practiceId: practiceId,
      patientId: patientId,
      departmentId: departmentId,
      providerId: providerId,
      attachmentContents: attachmentContents,
      programs : programs
    });
  }

  fetchCarePlanReportInfo(payload: any) : Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/report/care-plan-report`, {
      ...payload
    });
  }

  fetchScoreData(key : string) : Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/getScoreData`, {
      key
    });
  }

  updateProgramCompletion({patientId , prgmName , patientPrgmId , prgmClouserReason , consentId , prgmGrpId} : any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/program-complete` ,  {
      patientId,
      prgmName,
      patientPrgmId,
      prgmClouserReason : prgmClouserReason || '',
      consentId,
      prgmGrpId
    });
  }

  updateConsent({patientId ,patientProgramId ,programId , consent} : any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/update-consent` ,  {
      patientId,
      patientProgramId,
      programId,
      consent
    });
  }

  updateCompletionReason({consent_id , completion_reason} : any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/enrollmentDate` ,  {
      consent_id,
      completion_reason
    });
  }

  programEnroll({prgmGrpId , prgmMetadata , patientId , patientPrgmId , prgmGrpMetadata} : any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/program/enroll` ,  {
      patientId,
      prgmMetaData : prgmMetadata,
      prgmGrpMetadata,
      prgmGrpId,
      patientPrgmId
    });
  }

  programDisEnroll({patientId , consentId} : any): Observable<any> {
    return post(`${sessionService.getClient()?.appServiceUrl}/care-coordination/program/disenroll` ,  {
      patientId,
      consentId
    });
  }
}

export default new CareCoordinationService();
